import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

export default function SANewRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const resubmittedFrom = location.state?.resubmitted_from || null;

  const [form, setForm] = useState({
    account_number: location.state?.account_number || '',
    account_name: location.state?.account_name || '',
    previous_salary_month: '',
    previous_salary_amount: '',
    amount_requested: '',
    current_commitment: '',
    notes: '',
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.account_number || !form.account_name || !form.previous_salary_month || !form.previous_salary_amount || !form.amount_requested) {
      return toast.error('Please fill all required fields');
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => v && data.append(k, v));
      if (resubmittedFrom) data.append('resubmitted_from', resubmittedFrom);
      files.forEach(f => data.append('attachments', f));
      const res = await api.post('/api/sa', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`✅ Request ${res.data.request.request_number} submitted! Your Branch Manager has been notified.`);
      navigate(`/sa/${res.data.request.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:14 }} onClick={() => navigate('/sa')}>← Back</button>

      <div className="form-card">
        <div className="form-card-title">
          {resubmittedFrom ? '🔄 Resubmit Salary Advance Request' : '➕ New Salary Advance / Overdraft Request'}
        </div>

        {resubmittedFrom && (
          <div className="box box-orange" style={{ marginBottom:16 }}>
            ℹ️ You are resubmitting a previously declined request. Please update the details as needed.
          </div>
        )}

        <div className="info-box" style={{ marginBottom:16 }}>
          📋 Your request will first go to your <strong>Branch Manager</strong> for recommendation, then to your assigned <strong>Approver</strong> for final decision.
        </div>

        <form onSubmit={e => e.preventDefault()}>
          {/* Account Details */}
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Account Information</div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Account Number <span className="req">*</span></label>
              <input className="form-input" placeholder="e.g. 0123456789"
                value={form.account_number} onChange={e => set('account_number', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Name <span className="req">*</span></label>
              <input className="form-input" placeholder="Full name on account"
                value={form.account_name} onChange={e => set('account_name', e.target.value)} />
            </div>
          </div>

          {/* Salary Details */}
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:10, marginTop:4 }}>Salary Information</div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Previous Salary Month <span className="req">*</span></label>
              <input className="form-input" placeholder="e.g. April 2026"
                value={form.previous_salary_month} onChange={e => set('previous_salary_month', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Previous Salary Amount (₦) <span className="req">*</span></label>
              <input className="form-input" type="number" placeholder="e.g. 150000"
                value={form.previous_salary_amount} onChange={e => set('previous_salary_amount', e.target.value)} />
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Amount Requested (₦) <span className="req">*</span></label>
              <input className="form-input" type="number" placeholder="Amount you are requesting"
                value={form.amount_requested} onChange={e => set('amount_requested', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Commitment <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
              <input className="form-input" placeholder="e.g. Existing loan deductions, etc."
                value={form.current_commitment} onChange={e => set('current_commitment', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Additional Notes <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <textarea className="form-textarea" style={{ minHeight:80 }}
              placeholder="Any additional information or reason for the request…"
              value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Supporting Documents <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <div className="upload-zone" onClick={() => document.getElementById('sa-file-input').click()}>
              <div style={{ fontSize:24, marginBottom:6 }}>📎</div>
              <div>Click to attach supporting documents</div>
              <div style={{ marginTop:4, fontSize:11 }}>Images, PDFs, Word docs — max 10MB each</div>
              {files.length > 0 && (
                <div style={{ marginTop:8 }}>
                  {files.map((f,i) => (
                    <span key={i} style={{ fontSize:11, color:'var(--sky2)', background:'var(--sky-light)', display:'inline-block', padding:'2px 8px', borderRadius:4, margin:2 }}>
                      📄 {f.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <input id="sa-file-input" type="file" multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              style={{ display:'none' }} onChange={e => setFiles([...e.target.files])} />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Submitting…' : '🚀 Submit Request'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => navigate('/sa')}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
